import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import GetInfoService from "../../../../../service/GetInfoService";
import OrderService from "../../../../../service/OrderService";
import { removelocalStorageToken } from "../../../../../utilities/sessionStorage/token";
import { removelocalStorageUsername } from "../../../../../utilities/sessionStorage/username";
import { useToggle } from "../../../../../hooks/useToggle";
import IospediscoService from "../../../../../service/iospedisco";
import { usePayment } from "../context/PaymentContext";
import { useContext } from "react";
import { CartContext } from "../../../../../contexts/CartContext/CartContext";

const useCheckout = ({
  setAddressInfo,
  setPriceForex,
  setDisplayPaymentModal,
  setAmountShipmentForexProduct,
  setAmountTransfer,
}) => {
  const {
    order,
    setOrder,
    currencyProduct,
    sendMethod,
    setSendMethod,
    setPrice,
    amountWallet,
    amountBankTransfer,
    setAmountBankTransfer,
    bankTransferReceipt,
    setBankTransferReceipt,
    amountToPayWithExtracredit,
    setAmountToPayWithExtracredit,
    price,
    currencyCode: currencyCodeUser,
    userInfo,
    setAmountWallet,
    selectedPaymentMethod,
    amountToPayWithCreditCard,
    setPage,
    setCompleteAddress,
    address,
    city,
    country,
    district,
    number,
    postalCode,
    state,
    amountShipmentForexProduct,
  } = usePayment();
  const { clearCart } = useContext(CartContext);

  const location = useLocation();

  const isFringeBenefitsPage = location?.pathname?.toLowerCase()?.includes("fringe");

  const orderKey = isFringeBenefitsPage
    ? "__extraconomy_order_fringe_benefit"
    : "__extraconomy_order";

  const [loading, setLoading] = useState(false);

  const { showErrorToast } = useToastContext();
  const [isPickup, setIsPickup] = useState(() => {
    const saved = localStorage.getItem("__extraconomy_isPickup");
    return saved === "true";
  });

  const isPickupRef = useRef(isPickup);

  useEffect(() => {
    isPickupRef.current = isPickup;
  }, [isPickup]);

  const toggleIsPickup = useCallback(() => {
    const newValue = !isPickup;
    setIsPickup(newValue);
    localStorage.setItem("__extraconomy_isPickup", newValue.toString());
  }, [isPickup]);

  const [amountShipmentForexUser, setAmountShipmentForexUser] = useState(0);
  const [isFreeFreight, setIsFreeFreight] = useState(false);
  const [isLoadingFreight, setIsLoadingFreight] = useState(false);

  const [showMessageSellerResponsibility, setShowMessageSellerResponsibility] = useState(false);
  const [shippingQuotes, setShippingQuotes] = useState(null);

  const getInfoService = useMemo(() => new GetInfoService(), []);
  const orderService = useMemo(() => new OrderService(), []);
  const iospediscoService = useMemo(() => new IospediscoService(), []);

  const history = useHistory();

  const prevIsPickupRef = useRef(isPickup);

  const latestAmountWalletRef = useRef(amountWallet);
  const latestAmountExtracreditRef = useRef(amountToPayWithExtracredit);
  const latestAmountBankTransferRef = useRef(amountBankTransfer);
  latestAmountWalletRef.current = amountWallet;
  latestAmountExtracreditRef.current = amountToPayWithExtracredit;
  latestAmountBankTransferRef.current = amountBankTransfer;

  async function checkZipCode(zip) {
    if (zip.length > 0) {
      try {
        const zip_ = zip.replace(/\D+/g, "");

        setLoading(true);

        const userCountry = await getInfoService.getUserCountry();
        const res = await getInfoService.getZipCodeInfo(zip_, userCountry);

        if (Array.isArray(res.results) && res.results.length === 0) {
          setCompleteAddress({
            address: address || "",
            city: "",
            country: country || "",
            district: "",
            number: number || "",
            postalCode: postalCode || "",
            state: "",
          });
          setLoading(false);
        } else if (res.results[`${zip_}`][0]) {
          setCompleteAddress({
            address: address || "",
            city: res.results[`${zip_}`][0].province,
            country: country || "",
            district: res.results[`${zip_}`][0].city,
            number: number || "",
            postalCode: postalCode || "",
            state: res.results[`${zip_}`][0].province,
          });
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error checking zip code:", error);
        setLoading(false);
      }
    }
  }

  async function validateCheckout(finish) {
    setLoading(true);

    if (!order?.id) {
      showErrorToast({ detail: "Order information is missing." });
      setLoading(false);
      return;
    }

    if (isFringeBenefitsPage) {
      finish ? finishOrder() : paymentCheckout();
      return;
    }

    if (
      !order?.itens?.[0]?.product?.subCategory?.service &&
      !order?.itens?.[0]?.product?.downloadable &&
      order?.seller?.addressStatus === "ENABLED" &&
      !showMessageSellerResponsibility &&
      !shippingQuotes &&
      !isPickup
    ) {
      showErrorToast({ detail: "pleasePickASendMethod" });
      setLoading(false);
      return false;
    }

    if (
      !order?.itens?.[0]?.product?.downloadable &&
      !order?.itens?.[0]?.product?.service &&
      order?.seller?.addressStatus &&
      !["ENABLED", "IN_ANALYSIS"].includes(order?.seller?.addressStatus) &&
      !isPickup
    ) {
      setLoading(false);
      return false;
    }

    if (
      !order?.itens?.[0]?.product?.downloadable &&
      order?.itens?.[0]?.product?.shippingOption === "PAID" &&
      !sendMethod &&
      order?.seller?.addressStatus &&
      ["ENABLED", "IN_ANALYSIS"].includes(order?.seller?.addressStatus)
    ) {
      showErrorToast({ detail: "pleasePickASendMethod" });
      setLoading(false);
      return;
    }

    if (
      !order?.itens?.[0]?.product?.downloadable &&
      shippingQuotes &&
      !sendMethod &&
      !isPickup &&
      order?.seller?.addressStatus &&
      ["ENABLED", "IN_ANALYSIS"].includes(order?.seller?.addressStatus)
    ) {
      showErrorToast({ detail: "pleasePickASendMethod" });
      setLoading(false);
      return false;
    }

    if (
      (!order?.address ||
        !order?.city ||
        !order?.country ||
        !order?.district ||
        !order?.number ||
        !order?.postalCode ||
        !order?.state) &&
      !isPickup &&
      !order?.itens?.[0]?.product?.downloadable &&
      !order?.itens?.[0]?.product?.subCategory?.service &&
      !order?.itens?.[0]?.product?.service
    ) {
      showErrorToast({ detail: "pleaseFillAllFieldsAddressOrSearchShippingMethod" });
      setLoading(false);
      return;
    }

    if (isPickup) {
      const resShipping = await orderService.postAddPickup(
        address,
        city,
        country,
        district,
        0,
        postalCode,
        state,
        localStorage.getItem("__extraconomy_username"),
        order.id,
        null,
        localStorage.getItem("__extraconomy_token")
      );

      if (resShipping.messageCode === 200000) {
        const updatedOrder = {
          ...resShipping.result,
          amountShipment: 0,
        };
        setOrder(updatedOrder);
        setSendMethod(null);

        localStorage.setItem(orderKey, JSON.stringify(updatedOrder));
      }
      if (resShipping.messageCode !== 200000) {
        showErrorToast({ code: resShipping.messageCode });
        setLoading(false);
        return;
      }
    }

    finish ? finishOrder() : paymentCheckout();
  }

  const paymentCheckout = useCallback(async () => {
    if (!order?.id) {
      showErrorToast({ detail: "Order information is missing." });
      setLoading(false);
      return;
    }
    try {
      if (!isFringeBenefitsPage) {
        if (
          !order?.itens?.[0]?.product?.downloadable &&
          !order?.itens?.[0]?.product?.service &&
          (address.length === 0 ||
            city.length === 0 ||
            country.length === 0 ||
            district.length === 0 ||
            number.length === 0 ||
            postalCode.length === 0 ||
            state.length === 0) &&
          !isPickup
        ) {
          showErrorToast({ detail: "pleaseFillOutAll" });
          setLoading(false);
          return;
        }

        setAddressInfo({
          address: address,
          city: city,
          country: country,
          district: district,
          number: number,
          postalCode: postalCode,
          state: state,
        });
      }

      if (currencyCodeUser !== process.env.REACT_APP_BASE_PAYPAL_CURRENCY) {
        const resForex = await getInfoService.getForexRate(
          currencyCodeUser,
          process.env.REACT_APP_BASE_PAYPAL_CURRENCY
        );
        setPriceForex(parseFloat((price * resForex.result).toFixed(2)));
      } else setPriceForex(parseFloat(price.toFixed(2)));

      setDisplayPaymentModal(true);
      setLoading(false);
    } catch (e) {
      if (e.status === 401) {
        removelocalStorageToken();
        removelocalStorageUsername();
        history.push("/login");
      }
      setLoading(false);
    }
  }, [
    order?.id,
    isFringeBenefitsPage,
    address,
    city,
    country,
    district,
    number,
    postalCode,
    state,
    isPickup,
    setAddressInfo,
    currencyCodeUser,
    getInfoService,
    setPriceForex,
    price,
    setDisplayPaymentModal,
    showErrorToast,
    history,
  ]);

  const finishOrder = useCallback(async () => {
    if (!order?.id) {
      showErrorToast({ detail: "Order information is missing." });
      setLoading(false);
      setPage(5);
      return;
    }
    try {
      if (
        !isFringeBenefitsPage &&
        !order?.itens?.[0]?.product?.downloadable &&
        !order?.itens?.[0]?.product?.service &&
        (address.length === 0 ||
          city.length === 0 ||
          country.length === 0 ||
          district.length === 0 ||
          number.length === 0 ||
          postalCode.length === 0 ||
          state.length === 0) &&
        !isPickupRef.current
      ) {
        showErrorToast({ detail: "pleaseFillOutAll" });
        setLoading(false);
        return;
      }

      let res;

      if (isFringeBenefitsPage) {
        res = await orderService.postFinishFringeBenefitOrder(
          amountWallet != null ? parseFloat(amountWallet.toFixed(2)) : 0,
          amountToPayWithExtracredit != null
            ? parseFloat(amountToPayWithExtracredit.toFixed(2))
            : 0,
          amountBankTransfer != null ? parseFloat(amountBankTransfer.toFixed(2)) : 0,
          bankTransferReceipt,
          order.id,
          null,
          price != null ? price : 0
        );
      } else {
        if (isPickupRef.current) {
          resetShippingState();
          await removeShippingFromBackend();

          if (order?.amountShipment > 0) {
            setOrder((prevOrder) => {
              const newOrder = { ...prevOrder, amountShipment: 0 };
              localStorage.setItem(orderKey, JSON.stringify(newOrder));
              return newOrder;
            });
          }

          const currentDeductions = amountWallet + amountToPayWithExtracredit + amountBankTransfer;
          const calculatedPrice = Math.max(
            0,
            parseFloat(
              (order?.amountOrder + order?.amountToPlatform - currentDeductions).toFixed(2)
            )
          );
          setPrice(calculatedPrice);
        }

        const baseTotal = order?.amountOrder || 0;
        const shipmentPrice = isPickupRef.current ? 0 : order?.amountShipment || 0;
        const platformFee = order?.amountToPlatform || 0;
        const calculatedTotal = baseTotal + shipmentPrice + platformFee;
        const totalOrder = parseFloat((calculatedTotal || price).toFixed(2));
        const amountWalletValue = amountWallet ? parseFloat(amountWallet.toFixed(2)) : 0;
        const amountExtracredit = amountToPayWithExtracredit
          ? parseFloat(amountToPayWithExtracredit.toFixed(2))
          : 0;
        const amountBankTransferValue = amountBankTransfer
          ? parseFloat(amountBankTransfer.toFixed(2))
          : 0;
        const amountCreditCard = amountToPayWithCreditCard
          ? parseFloat(amountToPayWithCreditCard.toFixed(2))
          : 0;
        let amountCash = parseFloat(
          (
            totalOrder -
            amountWalletValue -
            amountExtracredit -
            amountBankTransferValue -
            amountCreditCard
          ).toFixed(2)
        );

        if (
          isPickupRef.current &&
          amountCash <= 0 &&
          totalOrder > 0 &&
          amountWalletValue === 0 &&
          amountExtracredit === 0 &&
          amountBankTransferValue === 0 &&
          amountCreditCard === 0
        ) {
          amountCash = totalOrder;
        }

        res = await orderService.postFinishOrder(
          localStorage.getItem("__extraconomy_token"),
          localStorage.getItem("__extraconomy_username"),
          address,
          amountCash,
          amountWalletValue,
          amountExtracredit,
          amountBankTransferValue,
          bankTransferReceipt,
          city,
          country,
          district,
          order.id,
          number,
          postalCode,
          state,
          null
        );
      }

      if (res.messageCode !== 200000) {
        showErrorToast({ code: res.messageCode });
        setLoading(false);
      } else {
        localStorage.removeItem(orderKey);
        localStorage.removeItem("__extraconomy_isPickup"); // Limpar isPickup do localStorage
        clearCart();
        setAmountTransfer(res.result?.amountTransferBank);
        if (res.result) {
          setOrder(res.result);
        }
        setPage(4);
      }
    } catch (e) {
      if (e.status === 401) {
        removelocalStorageToken();
        removelocalStorageUsername();
        history.push("/login");
      }
      setLoading(false);
    }
  }, [
    order?.id,
    isFringeBenefitsPage,
    address,
    city,
    country,
    district,
    number,
    postalCode,
    state,
    isPickup,
    amountWallet,
    amountToPayWithExtracredit,
    amountBankTransfer,
    amountToPayWithCreditCard,
    bankTransferReceipt,
    selectedPaymentMethod,
    orderService,
    showErrorToast,
    setPage,
    orderKey,
    setAmountTransfer,
    setOrder,
    clearCart,
    history,
  ]);

  const shippingQuote = async () => {
    if (!order?.id) {
      showErrorToast({ detail: "Order information is missing." });
      setLoading(false);
      setIsLoadingFreight(false);
      return;
    }

    if (isPickup) {
      setLoading(false);
      setIsLoadingFreight(false);
      return;
    }

    try {
      setLoading(true);
      setIsLoadingFreight(true);

      if (country === "IT" || country === "Itália" || country === "Italia") {
        if (!!order?.itens?.find((item) => item?.product?.shippingOption === "FREE")) {
          const resShipping = await orderService.postAddShipping(
            address,
            city,
            country,
            district,
            number,
            postalCode,
            state,
            localStorage.getItem("__extraconomy_username"),
            order.id,
            null,
            localStorage.getItem("__extraconomy_token"),
            0
          );
          if (resShipping.messageCode !== 200000) {
            if (resShipping.messageCode === 500143) {
              setSendMethod(null);
              setShippingQuotes(null);
              setShowMessageSellerResponsibility(true);
            } else {
              showErrorToast({ code: resShipping.messageCode });
            }
            setLoading(false);
            setIsLoadingFreight(false);
            return;
          }
          setIsFreeFreight(true);
          setShowMessageSellerResponsibility(true);
          setLoading(false);
          setIsLoadingFreight(false);
          return;
        } else if (
          !!order?.itens?.find((item) => item?.product?.shippingOption === "FREESENDCLOUD") &&
          !isPickup
        ) {
          if (!order?.seller) {
            showErrorToast({ detail: "errorCalculatingShipping" });
            setIsLoadingFreight(false);
            setLoading(false);
            return;
          }

          const res = await iospediscoService.getLocatiobId({
            token: localStorage.getItem("__extraconomy_token"),
            postal_code: postalCode,
            country: "IT",
          });
          const locationResult = res?.locationResponse?.location_results;

          if (locationResult && locationResult[0]?.locations_list[0]?.id_location) {
            const locationId = locationResult[0]?.locations_list[0]?.id_location;

            let pickupDate = order?.seller?.amountDaysToPickUp;

            if (typeof pickupDate === "string" && pickupDate.includes("-")) {
              pickupDate = 1;
            }

            if (!pickupDate || pickupDate === 0) {
              pickupDate = 1;
            }

            pickupDate = parseInt(pickupDate) || 1;

            const productBoxes = order?.itens.map((item, i) => ({
              index: i + 1,
              depth: item.product?.length || 1,
              height: item.product?.height || 1,
              weight: item.product?.weight || 0.1,
              width: item.product?.width || 1,
            }));

            const invalidProduct = productBoxes.find(
              (box) => box.depth <= 0 || box.height <= 0 || box.weight <= 0 || box.width <= 0
            );

            if (invalidProduct) {
              showErrorToast({ detail: "Produto com dimensões inválidas para cálculo de frete." });
              setIsLoadingFreight(false);
              setLoading(false);
              return;
            }

            const compare = await iospediscoService.compareCart({
              token: localStorage.getItem("__extraconomy_token"),
              id_location: locationId,
              pickup_date: pickupDate,
              boxes: productBoxes,
            });

            const freights = compare?.comparisonResponse?.comparison_result;
            if (freights) {
              const freightObj = freights[0]?.comparison_items[0];

              const freteMaisBarato = {
                idsendcloudmethod: `free_freight_${Date.now()}`,
                currency: "EUR",
                carrierMethod: freightObj?.courier,
                price: "free",
              };
              setSendMethod(freteMaisBarato);
              setShippingQuotes({ prices: [freteMaisBarato] });
              setLoading(false);
              setIsLoadingFreight(false);
              return;
            } else {
              showErrorToast({ detail: "errorCalculatingShipping" });
              setIsLoadingFreight(false);
              setLoading(false);
              return;
            }
          } else {
            showErrorToast({ detail: "locationNotFound" });
            setIsLoadingFreight(false);
            setLoading(false);
            return;
          }
        } else if (
          !isPickup &&
          (!!order?.itens?.find((item) => item?.product?.shippingOption === "PAID") ||
            !!order?.itens?.find((item) => item?.product?.shippingOption === "PICKUP"))
        ) {
          if (!order?.seller) {
            showErrorToast({ detail: "errorCalculatingShipping" });
            setIsLoadingFreight(false);
            setLoading(false);
            return;
          }

          const res = await iospediscoService.getLocatiobId({
            token: localStorage.getItem("__extraconomy_token"),
            postal_code: postalCode,
            country: "IT",
          });
          const locationResult = res?.locationResponse?.location_results;

          if (locationResult && locationResult[0]?.locations_list[0]?.id_location) {
            const locationId = locationResult[0]?.locations_list[0]?.id_location;

            let pickupDate = order?.seller?.amountDaysToPickUp;

            if (typeof pickupDate === "string" && pickupDate.includes("-")) {
              pickupDate = 1;
            }

            if (!pickupDate || pickupDate === 0) {
              pickupDate = 1;
            }

            pickupDate = parseInt(pickupDate) || 1;

            const compare = await iospediscoService.compareCart({
              token: localStorage.getItem("__extraconomy_token"),
              id_location: locationId,
              pickup_date: pickupDate,
              boxes: order?.itens.map((item, i) => ({
                index: i + 1,
                depth: item.product?.length,
                height: item.product?.height,
                weight: item.product?.weight,
                width: item.product?.width,
              })),
            });

            const freights = compare?.comparisonResponse?.comparison_result;
            if (freights) {
              const freightObj = freights[0]?.comparison_items[0];

              const freteMaisBarato = {
                idsendcloudmethod: freightObj?.id || `freight_${Date.now()}`,
                currency: "EUR",
                carrierMethod: freightObj?.courier,
                price: freightObj?.total_amount_tax_excluded,
              };
              setSendMethod(freteMaisBarato);
              setShippingQuotes({ prices: [freteMaisBarato] });
              setLoading(false);
              setIsLoadingFreight(false);
              return;
            } else {
              showErrorToast({ detail: "errorCalculatingShipping" });
              setIsLoadingFreight(false);
              setLoading(false);
              return;
            }
          } else {
            showErrorToast({ detail: "locationNotFound" });
            setIsLoadingFreight(false);
            setLoading(false);
            return;
          }
        }

        setShowMessageSellerResponsibility(true);
        setIsLoadingFreight(false);
        setLoading(false);
      } else {
        if (!!order?.itens?.find((item) => item?.product?.shippingOption === "FREE")) {
          setIsFreeFreight(true);
          setShowMessageSellerResponsibility(true);
          setLoading(false);
          setIsLoadingFreight(false);
          return;
        }

        setShowMessageSellerResponsibility(true);
        setIsLoadingFreight(false);
        setLoading(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        history.push("/login");
        return;
      }

      if (e.response?.data?.message) {
        showErrorToast({ detail: "errorCalculatingShipping" });
      } else if (e.response?.status === 400) {
        showErrorToast({ detail: "addValidPostalCode" });
      } else if (e.response?.status === 404) {
        showErrorToast({ detail: "locationNotFound" });
      } else if (e.code === "ERR_NETWORK" || e.message?.includes("Network")) {
        showErrorToast({ detail: "networkError" });
      } else {
        showErrorToast({ code: e.messageCode || 500000 });
      }

      setLoading(false);
      setIsLoadingFreight(false);
    }
  };

  const setAddressValues = () => {
    const emptyFields = [];
    if (!address || address.length === 0) emptyFields.push("address");
    if (!city || city.length === 0) emptyFields.push("city");
    if (!country || country.length === 0) emptyFields.push("country");
    if (!district || district.length === 0) emptyFields.push("district");
    if (!number || number.length === 0) emptyFields.push("number");
    if (!postalCode || postalCode.length === 0) emptyFields.push("postalCode");
    if (!state || state.length === 0) emptyFields.push("state");

    if (
      !order?.itens?.[0]?.product?.downloadable &&
      !order?.itens?.[0]?.product?.service &&
      emptyFields.length > 0
    ) {
      showErrorToast({ detail: "pleaseFillOutAll" });
      setLoading(false);
      return false;
    }
    setLoading(true);
    setIsLoadingFreight(true);

    const orderObj = order ? { ...order } : {};

    orderObj.postalCode = postalCode;
    orderObj.address = address;
    orderObj.city = city;
    orderObj.country = country;
    orderObj.district = district;
    orderObj.number = number;
    orderObj.state = state;

    localStorage.setItem(orderKey, JSON.stringify(orderObj));
    setOrder(orderObj);
    shippingQuote();
  };

  const setNewCompleteAddress = (address, city, country, district, number, postalCode, state) => {
    setCompleteAddress({
      address: address || "",
      city: city || "",
      country: country || "",
      district: district || "",
      number: number || "",
      postalCode: postalCode || "",
      state: state || "",
    });
  };

  useEffect(() => {
    let cancel = false;

    async function addShipping() {
      if (!order?.id) {
        setLoading(false);
        return;
      }

      if (isPickup) {
        setLoading(false);
        return;
      }

      if (order?.amountShipment && order.amountShipment > 0) {
        setLoading(false);
        return;
      }
      try {
        if (country === "IT" || country === "Itália" || country === "Italia") {
          setLoading(true);

          const seller = order?.seller;

          const validStatuses = ["ENABLED", "IN_ANALYSIS"];
          if (seller && !validStatuses.includes(seller.addressStatus)) {
            setSendMethod(null);
            setShippingQuotes(null);
            setShowMessageSellerResponsibility(true);
            setLoading(false);
            return;
          }

          if (sendMethod.price === "free") {
            const resShipping = await orderService.postAddShipping(
              address,
              city,
              country,
              district,
              Number(number),
              postalCode,
              state,
              localStorage.getItem("__extraconomy_username"),
              order.id,
              sendMethod.idsendcloudmethod,
              localStorage.getItem("__extraconomy_token"),
              0
            );
            if (resShipping.messageCode !== 200000) {
              if (resShipping.messageCode === 500143) {
                setSendMethod(null);
                setShippingQuotes(null);
                setShowMessageSellerResponsibility(true);
              } else {
                showErrorToast({ code: resShipping.messageCode });
              }
              setLoading(false);
              return;
            }

            setIsFreeFreight(true);
            setShowMessageSellerResponsibility(true);
            setLoading(false);
            return;
          }

          let sendMethodPrice = sendMethod.price;

          if (sendMethod?.currency !== currencyProduct) {
            const resRate = await getInfoService.getForexRate(sendMethod.currency, currencyProduct);
            sendMethodPrice = sendMethod.price * resRate.result;
          }

          const resShipping = await orderService.postAddShipping(
            address,
            city,
            country,
            district,
            number,
            postalCode,
            state,
            localStorage.getItem("__extraconomy_username"),
            order.id,
            sendMethod.idsendcloudmethod,
            localStorage.getItem("__extraconomy_token"),
            sendMethodPrice
          );
          if (cancel) return;

          if (resShipping.messageCode !== 200000) {
            if (resShipping.messageCode === 500143) {
              setSendMethod(null);
              setShippingQuotes(null);
              setShowMessageSellerResponsibility(true);
            } else {
              showErrorToast({ code: resShipping.messageCode });
            }
            setLoading(false);
            return false;
          }
          const actualAmountTotal =
            resShipping.result.amountTotal - resShipping.result.amountShipment;

          const orderObj = {
            ...resShipping.result,
            amountTotal: actualAmountTotal,
          };

          let amountShipmentForexProduct = resShipping.result.amountShipment;

          setAmountShipmentForexUser(amountShipmentForexProduct);
          setAmountShipmentForexProduct(amountShipmentForexProduct);

          setOrder((prevOrder) => {
            if (
              prevOrder?.id === orderObj.id &&
              prevOrder?.amountTotal === orderObj.amountTotal &&
              prevOrder?.amountShipment === orderObj.amountShipment
            ) {
              return prevOrder;
            }
            localStorage.setItem(orderKey, JSON.stringify(orderObj));
            return orderObj;
          });

          const priceToPay = parseFloat(
            (
              actualAmountTotal +
              amountShipmentForexProduct -
              (latestAmountWalletRef.current ?? 0) -
              (latestAmountExtracreditRef.current ?? 0) -
              (latestAmountBankTransferRef.current ?? 0)
            ).toFixed(2)
          );
          const newPrice = parseFloat(Math.abs(priceToPay).toFixed(2));
          setPrice((prevPrice) => {
            if (prevPrice === newPrice) {
              return prevPrice;
            }
            return newPrice;
          });

          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch (e) {
        if (e.status === 401) {
          removelocalStorageToken();
          removelocalStorageUsername();
          history.push("/login");
        }
        setLoading(false);
      }
    }

    if (sendMethod) {
      addShipping();
    }

    return () => {
      cancel = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sendMethod,
    order?.id,
    orderKey,
    address,
    city,
    country,
    district,
    number,
    postalCode,
    state,
    currencyProduct,
    isPickup,
  ]);

  useEffect(() => {
    const hasValidAddress =
      userInfo &&
      userInfo.address &&
      userInfo.city &&
      userInfo.country &&
      userInfo.district &&
      userInfo.number &&
      userInfo.postalCode &&
      userInfo.state;
    const hasNoExistingAddress = !address && !city && !district && !number && !postalCode && !state;

    if (hasValidAddress && hasNoExistingAddress) {
      setNewCompleteAddress(
        userInfo.address || "",
        userInfo.city || "",
        userInfo.country || "IT",
        userInfo.district || "",
        userInfo.number || "",
        userInfo.postalCode || "",
        userInfo.state || ""
      );
    }
  }, [userInfo]);

  const resetShippingState = useCallback(() => {
    setSendMethod(null);
    setShippingQuotes(null);
    setIsFreeFreight(false);
    setShowMessageSellerResponsibility(false);
    setIsLoadingFreight(false);
  }, [setSendMethod]);

  const removeShippingFromBackend = useCallback(async () => {
    if (!order?.id) {
      return;
    }

    try {
      await orderService.postAddShipping(
        address,
        city,
        country,
        district,
        number,
        postalCode,
        state,
        localStorage.getItem("__extraconomy_username"),
        order.id,
        null,
        localStorage.getItem("__extraconomy_token"),
        0
      );
    } catch (error) {
      console.error("Error removing shipping:", error);
    }
  }, [order?.id, address, city, country, district, number, postalCode, state, orderService]);

  const addPickupToBackend = useCallback(async () => {
    if (!order?.id) {
      return;
    }

    try {
      const resPickup = await orderService.postAddPickup(
        address,
        city,
        country,
        district,
        number,
        postalCode,
        state,
        localStorage.getItem("__extraconomy_username"),
        order.id,
        null,
        localStorage.getItem("__extraconomy_token")
      );

      if (resPickup.messageCode === 200000) {
        const updatedOrder = {
          ...resPickup.result,
          amountShipment: 0,
        };
        setOrder(updatedOrder);
        localStorage.setItem(orderKey, JSON.stringify(updatedOrder));
      }
    } catch (error) {
      console.error("Error adding pickup:", error);
    }
  }, [
    order?.id,
    address,
    city,
    country,
    district,
    number,
    postalCode,
    state,
    orderService,
    setOrder,
    orderKey,
  ]);

  const removePickupFromBackend = useCallback(async () => {
    if (!order?.id) {
      return;
    }

    try {
      const resRemovePickup = await orderService.postRemovePickup(
        address,
        city,
        country,
        district,
        number,
        postalCode,
        state,
        localStorage.getItem("__extraconomy_username"),
        order.id,
        null,
        localStorage.getItem("__extraconomy_token")
      );

      if (resRemovePickup.messageCode === 200000) {
        const resShipping = await orderService.postAddShipping(
          address,
          city,
          country,
          district,
          number,
          postalCode,
          state,
          localStorage.getItem("__extraconomy_username"),
          order.id,
          null,
          localStorage.getItem("__extraconomy_token"),
          0
        );

        if (resShipping.messageCode === 200000) {
          const updatedOrder = resShipping.result;
          setOrder(updatedOrder);
          localStorage.setItem(orderKey, JSON.stringify(updatedOrder));
        }
      }
    } catch (error) {
      console.error("Error removing pickup:", error);
    }
  }, [
    order?.id,
    address,
    city,
    country,
    district,
    number,
    postalCode,
    state,
    orderService,
    setOrder,
    orderKey,
  ]);

  useEffect(() => {
    if (isPickup && order?.amountShipment > 0) {
      setOrder((prevOrder) => {
        const newOrder = { ...prevOrder, amountShipment: 0 };
        localStorage.setItem(orderKey, JSON.stringify(newOrder));
        return newOrder;
      });
    }
  }, [isPickup, order?.amountShipment, orderKey]);

  useEffect(() => {
    const prevIsPickup = prevIsPickupRef.current;

    const handlePickupChange = async () => {
      if (isPickup && !prevIsPickup) {
        resetShippingState();
        await addPickupToBackend();
      } else if (!isPickup && prevIsPickup) {
        await removePickupFromBackend();
      }
    };

    if (order?.id && prevIsPickup !== isPickup) {
      handlePickupChange();
      prevIsPickupRef.current = isPickup;
    }
  }, [isPickup, order?.id, resetShippingState, addPickupToBackend, removePickupFromBackend]);

  useEffect(() => {
    if (isPickup && order) {
      const currentDeductions = amountWallet + amountToPayWithExtracredit + amountBankTransfer;
      const calculatedPrice = Math.max(
        0,
        parseFloat((order.amountOrder + order.amountToPlatform - currentDeductions).toFixed(2))
      );
      setPrice(calculatedPrice);
    }
  }, [isPickup, order, amountWallet, amountToPayWithExtracredit, amountBankTransfer, setPrice]);

  const resetOrderWhenChangeAddress = () => {
    setPrice((prev) => prev - order.amountShipment);
    setShippingQuotes(null);
    setSendMethod(null);
    setIsFreeFreight(false);
    setShowMessageSellerResponsibility(false);
  };

  useEffect(() => {
    return () => {
      setShippingQuotes(null);
      setSendMethod(null);
      setIsFreeFreight(false);
      setShowMessageSellerResponsibility(false);
      setAmountShipmentForexProduct(0);
      setAmountWallet(0);
    };
  }, []);

  return {
    validateCheckout,
    loading,
    amountShipmentForexUser,
    postalCode,
    shippingQuotes,
    isFreeFreight,
    number,
    setAddressValues,
    resetOrderWhenChangeAddress,
    showMessageSellerResponsibility,
    country,
    checkZipCode,
    state,
    city,
    district,
    toggleIsPickup,
    isPickup,
    setIsPickup,
    address,
    isLoadingFreight,
    amountToPayWithExtracredit,
    setAmountToPayWithExtracredit,
    amountBankTransfer,
    setAmountBankTransfer,
    bankTransferReceipt,
    setBankTransferReceipt,
    setNewCompleteAddress,
  };
};

export default useCheckout;
